import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { Loader2, ShieldCheck, MessageCircle, ArrowRight, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../../lib/api";
import { useSite } from "../../context/SiteContext";
import { inrCompact, waLink, track, newIdempotencyKey, EMPLOYMENT_TYPES, CIBIL_RANGES } from "../../lib/site";

export default function EligibilityChecker({ defaultType = "Personal Loan" }) {
  const { settings, products } = useSite();
  const idemKey = useMemo(newIdempotencyKey, []);
  const [form, setForm] = useState({
    loan_type: defaultType, age: 30, employment: "Salaried", income: "",
    existing_emi: "", amount: "", tenure_months: 60, city: "", cibil: "",
  });
  const [contact, setContact] = useState({ name: "", mobile: "", email: "" });
  const [savingLead, setSavingLead] = useState(false);
  const [leadSaved, setLeadSaved] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const setC = (k) => (e) => setContact((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (loading) return;
    setLoading(true);
    setResult(null);
    setLeadSaved(null);
    try {
      const { data } = await api.post("/eligibility/check", {
        ...form, age: Number(form.age), income: Number(form.income),
        existing_emi: Number(form.existing_emi) || 0, amount: Number(form.amount) || 0,
        tenure_months: Number(form.tenure_months),
      });
      track("eligibility_check", { product: form.loan_type });
      setResult(data);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  const saveLead = async (e) => {
    e.preventDefault();
    if (savingLead) return;
    setSavingLead(true);
    try {
      const p = new URLSearchParams(window.location.search);
      const { data } = await api.post("/eligibility/lead", {
        ...contact,
        loan_type: form.loan_type, age: Number(form.age), employment: form.employment,
        income: Number(form.income) || 0, existing_emi: Number(form.existing_emi) || 0,
        amount: Number(form.amount) || 0, tenure_months: Number(form.tenure_months),
        cibil: form.cibil, city: form.city, eligibility_result: result,
        page: window.location.pathname, idempotency_key: idemKey,
        utm_source: p.get("utm_source") || "", utm_medium: p.get("utm_medium") || "", utm_campaign: p.get("utm_campaign") || "",
      });
      track("lead_submit", { lead_id: data.lead_id, product: form.loan_type });
      setLeadSaved(data);
      toast.success("Saved. Our advisors will review your eligibility estimate.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingLead(false);
    }
  };

  return (
    <div data-testid="eligibility-checker" className="card-site p-6 sm:p-10">
      <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        <select data-testid="elig-loan-type" className="input-site" value={form.loan_type} onChange={set("loan_type")} aria-label="Loan type">
          {(products.length ? products.map((p) => p.name) : [defaultType]).map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input data-testid="elig-age" type="number" min="18" max="80" className="input-site" placeholder="Age *" value={form.age} onChange={set("age")} aria-label="Age" />
        <select data-testid="elig-employment" className="input-site" value={form.employment} onChange={set("employment")} aria-label="Employment type">
          {EMPLOYMENT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input required data-testid="elig-income" type="number" className="input-site" placeholder="Monthly Income (₹) *" value={form.income} onChange={set("income")} aria-label="Monthly income" />
        <input data-testid="elig-existing-emi" type="number" className="input-site" placeholder="Existing EMIs (₹/month)" value={form.existing_emi} onChange={set("existing_emi")} aria-label="Existing EMIs" />
        <input data-testid="elig-amount" type="number" className="input-site" placeholder="Desired Loan Amount (₹)" value={form.amount} onChange={set("amount")} aria-label="Desired loan amount" />
        <select data-testid="elig-tenure" className="input-site" value={form.tenure_months} onChange={set("tenure_months")} aria-label="Tenure">
          {[24, 36, 48, 60, 84, 120, 180, 240].map((t) => <option key={t} value={t}>{t} months</option>)}
        </select>
        <select data-testid="elig-cibil" className="input-site" value={form.cibil} onChange={set("cibil")} aria-label="CIBIL range">
          <option value="">CIBIL Range (optional)</option>
          {CIBIL_RANGES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
        <input data-testid="elig-city" className="input-site" placeholder="City" value={form.city} onChange={set("city")} aria-label="City" />
        <button type="submit" disabled={loading} data-testid="elig-submit-btn" className="btn-primary sm:col-span-2 lg:col-span-3 lg:w-max">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <>Check My Eligibility <ArrowRight size={15} /></>}
        </button>
      </form>

      {result && (
        <div data-testid="elig-result" className="mt-8 rounded-2xl border border-black/10 bg-brand-cream p-6 sm:p-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-6 justify-between">
            <div>
              <p className="text-xs uppercase tracking-[0.2em] font-bold text-brand-muted">Indicative Eligible Amount</p>
              <p data-testid="elig-amount-result" className="font-heading text-3xl sm:text-4xl font-extrabold text-brand-red mt-1">
                {result.eligible ? inrCompact(result.eligible_amount) : "—"}
              </p>
              {result.eligible > 0 && (
                <p className="text-sm text-brand-muted mt-1">Indicative EMI: <span className="font-bold text-brand-dark">{inrCompact(result.emi)}/mo</span> @ {result.rate_used}% for {result.tenure_months} months</p>
              )}
              <p className="text-sm text-brand-dark mt-3 max-w-xl leading-relaxed">{result.message}</p>
              {result.eligible > 0 && !result.requested_within_range && (
                <p data-testid="elig-range-warning" className="text-sm text-amber-700 mt-2">Your desired amount appears higher than the indicative estimate. Our advisors can review options.</p>
              )}
            </div>
            <div className="flex flex-col gap-3 shrink-0">
              <Link to="/apply" data-testid="elig-apply-btn" onClick={() => track("start_application", { product: form.loan_type })} className="btn-primary">Start Application</Link>
              <a href={waLink(settings, settings?.whatsapp_templates?.eligibility)} target="_blank" rel="noreferrer" onClick={() => track("whatsapp_click", { product: form.loan_type })} data-testid="elig-whatsapp-btn" className="btn-outline !border-[#1FA855]/40 !text-[#1FA855]">
                <MessageCircle size={15} /> Discuss on WhatsApp
              </a>
            </div>
          </div>

          {result.eligible > 0 && !leadSaved && (
            <form onSubmit={saveLead} className="mt-6 pt-6 border-t border-black/10" data-testid="elig-lead-form">
              <p className="font-heading font-bold text-brand-dark text-sm">Get this estimate reviewed by an expert — free.</p>
              <div className="grid sm:grid-cols-3 gap-3 mt-3">
                <input required data-testid="elig-lead-name" className="input-site !bg-white" placeholder="Full Name *" value={contact.name} onChange={setC("name")} aria-label="Full name" />
                <input required data-testid="elig-lead-mobile" className="input-site !bg-white" placeholder="Mobile Number *" value={contact.mobile} onChange={setC("mobile")} inputMode="tel" aria-label="Mobile number" />
                <input data-testid="elig-lead-email" type="email" className="input-site !bg-white" placeholder="Email (optional)" value={contact.email} onChange={setC("email")} aria-label="Email" />
              </div>
              <button type="submit" disabled={savingLead} data-testid="elig-lead-submit-btn" className="btn-primary mt-4 !py-2.5 !text-xs">
                {savingLead ? <Loader2 size={14} className="animate-spin" /> : "Save My Result & Get a Call"}
              </button>
            </form>
          )}
          {leadSaved && (
            <div className="mt-6 pt-6 border-t border-black/10 flex items-center gap-3" data-testid="elig-lead-success">
              <CheckCircle2 size={20} className="text-emerald-600 shrink-0" />
              <p className="text-sm text-brand-dark">Saved as lead <span className="font-heading font-extrabold text-brand-red">{leadSaved.lead_id}</span>. Our team will contact you to discuss suitable options.</p>
            </div>
          )}
          <p className="mt-5 pt-4 border-t border-black/10 text-xs text-brand-muted flex items-start gap-2">
            <ShieldCheck size={14} className="mt-0.5 shrink-0 text-brand-gold" /> {result.disclaimer}
          </p>
        </div>
      )}
    </div>
  );
}
